import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { useEffect, lazy, Suspense } from 'react'
import { AuthProvider, useAuth } from './context/AuthContext'
import { useCartStore } from './stores/cartStore'
import ProtectedRoute from './components/ProtectedRoute'
import ScrollRestoration from './components/ScrollRestoration'
import MobileNav from './components/MobileNav'
import PWAUpdateBanner from './components/PWAUpdateBanner'
import PWAInstallButton from './components/PWAInstallButton'
import NotificationToast from './components/NotificationToast'
import PushBridge from './components/PushBridge'
import ProfileGate from './components/ProfileGate'
import TermsGate from './components/TermsGate'
import SiteOnly from './components/SiteOnly'
import CompleteProfile from './pages/CompleteProfile'
import MessageToast from './components/MessageToast'
import HomeRoute from './components/HomeRoute'
import HowItWorks from './pages/HowItWorks'
import About from './pages/About'
import Browse from './pages/Browse'
import Login from './pages/Login'
import Register from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import AuthCallback from './pages/AuthCallback'
import TermsOfUse from './pages/TermsOfUse'
import PrivacyPolicy from './pages/PrivacyPolicy'
import AcceptableUse from './pages/AcceptableUse'
import UserDashboard from './pages/UserDashboard'
import Profile from './pages/Profile'
import ListItem from './pages/ListItem'
import VerifyEmail from './pages/VerifyEmail'
import CompleteRegistration from './pages/CompleteRegistration'
import ItemDetail from './pages/ItemDetail'
import EditItem from './pages/EditItem'
import Cart from './pages/Cart'
import CheckoutSuccess from './pages/CheckoutSuccess'
import MyRentals from './pages/MyRentals'
import Earnings from './pages/Earnings'
import Messages from './pages/Messages'
import Notifications from './pages/Notifications'
import Offline from './pages/Offline'
import RentalDetail from './pages/RentalDetail'
import PickupTracker from './components/fulfillment/PickupTracker'
import ShippingTracker from './components/fulfillment/ShippingTracker'
import QRCodeScanner from './components/fulfillment/QRCodeScanner'
import EquipmentInspection from './components/fulfillment/EquipmentInspection'
import DigitalHandshake from './components/fulfillment/DigitalHandshake'
import './index.css'
import './App.css'

// Lazy-loaded: @stripe/stripe-js injects its own <script src="js.stripe.com">
// tag as a side effect of merely being imported, regardless of whether it's
// ever used. Since the rest of the app is one eagerly-loaded bundle, that
// meant Stripe started loading (and tripping CSP/tracking-protection
// warnings) on every single page, not just checkout.
const Checkout = lazy(() => import('./pages/Checkout'))
// Admin code is only ever needed by admins, so it stays out of everyone else's download.
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))

function CartSync() {
  const { user } = useAuth()
  const syncCart = useCartStore((s) => s.syncCart)

  useEffect(() => {
    if (user) {
      syncCart()
    }
  }, [user, syncCart])

  return null
}

function App() {
  return (
    <AuthProvider>
      <CartSync />
      <BrowserRouter>
        <ProfileGate />
        <TermsGate />
        <ScrollRestoration />
        <PushBridge />
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/about" element={<About />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/complete-registration" element={<CompleteRegistration />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/acceptable-use" element={<AcceptableUse />} />
          <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/list-item" element={<ProtectedRoute><ListItem /></ProtectedRoute>} />
          <Route path="/item/:id" element={<ItemDetail />} />
          <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
          <Route path="/checkout" element={<ProtectedRoute><Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}><Checkout /></Suspense></ProtectedRoute>} />
          <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccess /></ProtectedRoute>} />
          <Route path="/my-rentals" element={<ProtectedRoute><MyRentals /></ProtectedRoute>} />
          <Route path="/earnings" element={<ProtectedRoute><Earnings /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/offline" element={<Offline />} />
          <Route path="/rental/:id" element={<ProtectedRoute><RentalDetail /></ProtectedRoute>} />
          <Route path="/edit-item/:id" element={<ProtectedRoute><EditItem /></ProtectedRoute>} />
          {/* Fulfillment Routes */}
          <Route path="/fulfillment/:id" element={<ProtectedRoute><PickupTracker /></ProtectedRoute>} />
          <Route path="/fulfillment/:id/shipping" element={<ProtectedRoute><ShippingTracker /></ProtectedRoute>} />
          <Route path="/fulfillment/:id/scan" element={<ProtectedRoute><QRCodeScanner /></ProtectedRoute>} />
          <Route path="/fulfillment/:id/inspect" element={<ProtectedRoute><EquipmentInspection /></ProtectedRoute>} />
          <Route path="/fulfillment/:id/inspection" element={<ProtectedRoute><EquipmentInspection /></ProtectedRoute>} />
          <Route path="/fulfillment/:id/handshake" element={<ProtectedRoute><DigitalHandshake /></ProtectedRoute>} />
          <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute requireRole="ADMIN"><Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}><AdminDashboard /></Suspense></ProtectedRoute>} />
        </Routes>
        <MobileNav />
        <PWAUpdateBanner />
        <SiteOnly>
          <PWAInstallButton />
          <NotificationToast />
          <MessageToast />
        </SiteOnly>
      </BrowserRouter>
    </AuthProvider>
  )
}

export default App
